import type { CanvasLayer, FrameContext } from "./canvasLayer";
import { dpr } from "./dpr";

export interface FloatingPopupBase {
	elapsed: number;
	screenX: number;
	screenY: number;
}

// Shared lifecycle for the pickup popups (loot, gold): a small box anchored over
// a cell that floats upward and fades out over a fixed lifetime. Subclasses keep
// their own content, constants, and durations; the base owns the float/fade math.
export abstract class FloatingPopupRenderer<P extends FloatingPopupBase> implements CanvasLayer {
	protected popups: P[] = [];

	// Lifetime of a single popup (ms), per subclass.
	protected abstract durationMs: number;
	// Fraction of the lifetime at which the popup starts fading out (0.0–1.0).
	protected fadeStart = 0.85;
	// Total pixels the popup floats upward over its lifetime (CSS pixels, scaled by dpr).
	protected floatDistance = 30;

	passTime(rawElapsedMs: number): void {
		for (const popup of this.popups) {
			popup.elapsed += rawElapsedMs;
		}
		this.popups = this.popups.filter((p) => p.elapsed < this.durationMs);
	}

	render(ctx: CanvasRenderingContext2D, _frame: FrameContext): void {
		const ratio = dpr();
		const floatDistance = this.floatDistance * ratio;

		for (const popup of this.popups) {
			const t = popup.elapsed / this.durationMs;
			const yOffset = -floatDistance * t;
			const alpha = t < this.fadeStart ? 1.0 : 1.0 - (t - this.fadeStart) / (1.0 - this.fadeStart);

			ctx.save();
			ctx.globalAlpha = alpha;
			this.drawContent(ctx, popup, yOffset, ratio);
			ctx.restore();
		}
	}

	// Draws the subclass box + content at the faded/offset position. Alpha is
	// already applied to the context; add yOffset to the popup's screenY.
	protected abstract drawContent(
		ctx: CanvasRenderingContext2D,
		popup: P,
		yOffset: number,
		ratio: number,
	): void;

	clear(): void {
		this.popups = [];
	}

	hasPopups(): boolean {
		return this.popups.length > 0;
	}
}
